// src/Account.ts
var DEFAULT_ACCOUNT_LABEL = "Account";

// src/AccountOperations.ts
import { CustomError } from "@umami/utils";
import { BigNumber } from "bignumber.js";
var totalFee = (estimates) => estimates.map((estimate2) => estimate2.fee).reduce((acc, curr) => acc.plus(curr), BigNumber(0)).toNumber();
var makeAccountOperations = (sender, signer, operations) => {
  switch (sender.type) {
    case "ledger":
    case "mnemonic":
    case "social":
    case "secret_key":
      if (sender.address.pkh !== signer.address.pkh) {
        throw new CustomError("Sender and Signer must be the same");
      }
      return {
        type: "implicit",
        operations,
        signer: sender,
        sender
      };
    case "multisig":
      return {
        type: "proposal",
        operations,
        sender,
        signer
      };
  }
};

// src/constants/Hints.ts
var finalizationConsequences = "Finalizing unstaked funds makes them available on your spendable balance. They can now be spent or staken again to earn more rewards.";
var unstakingConsequences = `Unstaked tez immediately stop accruing staking rewards, but sill give rights to delegation rewards. Unstaked tez remain locked for up to 4 cycles (~11 days), after which they can be finalized on request. ${finalizationConsequences}`;
var Hints = {
  delegation: {
    header: "It takes at least 3 full cycles (~9 days) to start receiving delegation rewards",
    description: "The actual delay will depend on your chosen baker payout strategy. Bakers typically distribute delegation rewards every cycle (~3 days). Ensure the delegation fee offered by the baker is less than 100%; otherwise, you may not receive rewards. Delegation means that all your funds are delegated to one baker. The funds remain spendable, and you can cancel or change the delegation at any time."
  },
  undelegation: {
    header: "Stops both delegation and staking. Restoring rewards takes 2 cycles (~6 days).",
    description: `Undelegation takes effect immediately. However, you will still receive delegation rewards for the current and the next 2 cycles, as baking rights are computed 2 cycles in advance. By undelegating, you also initiate the unstaking process if you have staked. ${unstakingConsequences}`
  },
  stake: {
    header: "You will start receiving rewards immediately (within the current cycle)",
    description: `Your spendable balance will be reduced by the amount staked. You will start accruing staking rewards immediately (within the current cycle). Staking rewards are compounded on your staked balance. You can cancel staking any time. ${unstakingConsequences}`
  },
  unstake: {
    header: "Unstake request take effect immediately, but funds remain frozen for up to 4 cycles (~10 days)",
    description: unstakingConsequences
  },
  finalize_unstake: {
    header: "Takes 1 block (8 seconds) to complete.",
    description: `Finalizing applies to all finalizable funds in your balance. ${finalizationConsequences}`
  }
};

// src/constants/Titles.ts
var Titles = {
  tez: "Send request",
  contract_call: "Contract call request",
  delegation: "Delegation request",
  undelegation: "Undelegation request",
  contract_origination: "Origination request",
  stake: "Stake request",
  unstake: "Unstake request",
  finalize_unstake: "Finalize unstake Request",
  batch: "Batch request",
  approve: "Approval request"
};

// src/decodeBeaconPayload.ts
import { CODEC, Uint8ArrayConsumer, getCodec } from "@taquito/local-forging";
import { DefaultProtocol, unpackData } from "@taquito/michel-codec";
import { bytesToString, hex2buf } from "@taquito/utils";
import { SigningType } from "@tezos-x/octez.connect-wallet";

// ../utils/src/ErrorContext.ts
import { TezosOperationError } from "@taquito/taquito";
import sanitizeHtml from "sanitize-html";
var CustomError2 = class extends Error {
  constructor(message) {
    super(message);
    this.name = "CustomError";
  }
};

// src/decodeBeaconPayload.ts
var decodeBeaconPayload = (payload, signingType) => {
  try {
    if (!payload.length) {
      return { result: "" };
    }
    let result = payload;
    switch (signingType) {
      case SigningType.MICHELINE:
      case SigningType.OPERATION: {
        if (getSigningTypeFromPayload(payload) !== signingType) {
          throw new CustomError2("Invalid prefix for signing type");
        }
        try {
          const consumer = Uint8ArrayConsumer.fromHexString(payload);
          const uint8array = consumer.consume(consumer.length());
          const parsed = unpackData(uint8array);
          if ("string" in parsed && Object.keys(parsed).length === 1) {
            result = parsed.string;
          } else {
            result = JSON.stringify(parsed);
          }
        } catch {
          const prefixLen = signingType === SigningType.MICHELINE ? 12 : 2;
          result = bytesToString(payload.slice(prefixLen));
          if (result.length === 0) {
            throw new CustomError2("Invalid payload. Failed to decode.");
          }
        }
        break;
      }
      case SigningType.RAW: {
        try {
          result = JSON.stringify(parseOperationMicheline(payload));
        } catch {
          result = new TextDecoder("utf-8", { fatal: true }).decode(hex2buf(payload));
        }
        break;
      }
      default: {
        throw new CustomError2(`Unsupported signing type: ${signingType}`);
      }
    }
    if (!isValidASCII(result)) {
      throw new CustomError2("Invalid payload. Only ASCII characters are supported.");
    }
    return { result };
  } catch (error) {
    console.error(error);
    return { result: payload, error: "Cannot parse Beacon payload" };
  }
};
var isValidASCII = (str) => str.split("").every((char) => char.charCodeAt(0) < 128);
var parseOperationMicheline = (payload) => getCodec(CODEC.MANAGER, DefaultProtocol).decoder(payload);
var getSigningTypeFromPayload = (payload) => payload.startsWith("05") ? SigningType.MICHELINE : payload.startsWith("03") ? SigningType.OPERATION : SigningType.RAW;

// src/estimate.ts
import { isAccountRevealed, makeToolkit } from "@umami/tezos";

// src/helpers.ts
import { OpKind } from "@taquito/rpc";
import { isValidMichelson } from "@umami/tezos";
import { CustomError as CustomError4 } from "@umami/utils";
import { shuffle, zipWith } from "lodash";

// src/Operation.ts
import { MANAGER_LAMBDA } from "@taquito/taquito";
import { CustomError as CustomError3 } from "@umami/utils";
import { isEqual } from "lodash";
var SafeSendAssetType = /* @__PURE__ */ ((SafeSendAssetType2) => {
  SafeSendAssetType2["TEZ"] = "tez";
  SafeSendAssetType2["FA12"] = "fa1.2";
  SafeSendAssetType2["FA2"] = "fa2";
  return SafeSendAssetType2;
})(SafeSendAssetType || {});
var LAMBDA_HEADER = [
  { prim: "DROP" },
  { prim: "NIL", args: [{ prim: "operation" }] }
];
var makeMultisigApproveOrExecuteOperation = (contract, entrypoint, operationId) => makeContractCallOperation(contract, entrypoint, {
  int: operationId
});
var makeMultisigProposeOperation = (contract, proposedOperations) => {
  const lambdaActions = toBatchLambda(proposedOperations);
  return makeContractCallOperation(contract, "propose", lambdaActions);
};
var makeContractCallOperation = (contract, entrypoint, args, amount = "0") => ({
  type: "contract_call",
  contract,
  entrypoint,
  args,
  amount
});
var headlessLambda = (lambda) => {
  if (isEqual(lambda.slice(0, 2), LAMBDA_HEADER)) {
    return lambda.slice(2);
  }
  return lambda;
};
var toLambda = (operation) => {
  switch (operation.type) {
    case "tez":
      switch (operation.recipient.type) {
        case "implicit":
          return MANAGER_LAMBDA.transferImplicit(operation.recipient.pkh, Number(operation.amount));
        case "contract":
          return MANAGER_LAMBDA.transferToContract(
            operation.recipient.pkh,
            Number(operation.amount)
          );
        default:
          throw new CustomError3(`${operation.recipient.type} is not supported yet`);
      }
    // eslint-disable-next-line no-fallthrough
    case "fa1.2":
      return contractLambda(
        operation,
        FA12_TRANSFER_ARG_TYPES,
        makeFA12TransactionParameter(operation)
      );
    case "fa2": {
      return contractLambda(
        operation,
        FA2_TRANSFER_ARG_TYPES,
        makeFA2TransactionParameter(operation)
      );
    }
    case "delegation":
      return MANAGER_LAMBDA.setDelegate(operation.recipient.pkh);
    case "undelegation":
      return MANAGER_LAMBDA.removeDelegate();
    case "contract_origination":
    case "contract_call":
    case "stake":
    case "unstake":
    case "finalize_unstake":
    case "safe_send":
    case "claim_safe_send":
    case "withdraw_safe_send":
    case "personal_sign":
    case "eth_sendTransaction":
    case "approve":
      throw new CustomError3(`${operation.type} is not supported yet`);
  }
};
var toBatchLambda = (operations) => {
  const opsLambdas = operations.map(toLambda).flatMap(headlessLambda);
  return [...LAMBDA_HEADER, ...opsLambdas];
};
var FA2_TRANSFER_ARG_TYPES = {
  args: [
    {
      args: [
        {
          annots: ["%from_"],
          prim: "address"
        },
        {
          annots: ["%txs"],
          args: [
            {
              args: [
                {
                  annots: ["%to_"],
                  prim: "address"
                },
                {
                  args: [
                    {
                      annots: ["%token_id"],
                      prim: "nat"
                    },
                    {
                      annots: ["%amount"],
                      prim: "nat"
                    }
                  ],
                  prim: "pair"
                }
              ],
              prim: "pair"
            }
          ],
          prim: "list"
        }
      ],
      prim: "pair"
    }
  ],
  prim: "list"
};
var FA12_TRANSFER_ARG_TYPES = {
  args: [
    {
      annots: [":from"],
      prim: "address"
    },
    {
      args: [
        {
          annots: [":to"],
          prim: "address"
        },
        {
          annots: [":value"],
          prim: "nat"
        }
      ],
      prim: "pair"
    }
  ],
  prim: "pair"
};
var contractLambda = (operation, argTypes, transactionParameter) => [
  ...LAMBDA_HEADER,
  {
    prim: "PUSH",
    args: [
      { prim: "address" },
      { string: operation.contract.pkh + "%" + transactionParameter.entrypoint }
    ]
  },
  {
    prim: "CONTRACT",
    args: [argTypes]
  },
  // If contract is not valid then fail and rollback the whole transaction
  [{ prim: "IF_NONE", args: [[{ prim: "UNIT" }, { prim: "FAILWITH" }], []] }],
  { prim: "PUSH", args: [{ prim: "mutez" }, { int: "0" }] },
  { prim: "PUSH", args: [argTypes, transactionParameter.value] },
  { prim: "TRANSFER_TOKENS" },
  { prim: "CONS" }
];
var makeFA12TransactionParameter = ({
  sender,
  recipient,
  amount
}) => ({
  entrypoint: "transfer",
  value: {
    prim: "Pair",
    args: [
      {
        string: sender.pkh
      },
      {
        prim: "Pair",
        args: [
          {
            string: recipient.pkh
          },
          {
            int: amount
          }
        ]
      }
    ]
  }
});
var makeFA2TransactionParameter = ({
  sender,
  recipient,
  tokenId,
  amount
}) => ({
  entrypoint: "transfer",
  value: [
    {
      prim: "Pair",
      args: [
        {
          string: sender.pkh
        },
        [
          {
            prim: "Pair",
            args: [
              {
                string: recipient.pkh
              },
              {
                prim: "Pair",
                args: [
                  {
                    int: tokenId
                  },
                  {
                    int: amount
                  }
                ]
              }
            ]
          }
        ]
      ]
    }
  ]
});
var operationSign = (destination) => {
  switch (destination) {
    case "incoming":
      return "+";
    case "outgoing":
      return "-";
    case "unrelated":
      return "";
  }
};

// src/helpers.ts
var selectRandomElements = (arr, n) => shuffle(arr.map((value, index) => ({ value, index }))).slice(0, n).sort((a, b) => a.index - b.index);
var operationToTaquitoOperation = (operation) => {
  switch (operation.type) {
    case "tez":
      return {
        kind: OpKind.TRANSACTION,
        to: operation.recipient.pkh,
        amount: parseInt(operation.amount),
        mutez: true
      };
    case "contract_call":
      return {
        kind: OpKind.TRANSACTION,
        to: operation.contract.pkh,
        amount: parseInt(operation.amount),
        mutez: true,
        parameter: { entrypoint: operation.entrypoint, value: operation.args }
      };
    case "delegation":
      return {
        kind: OpKind.DELEGATION,
        source: operation.sender.pkh,
        delegate: operation.recipient.pkh
      };
    case "undelegation":
      return {
        kind: OpKind.DELEGATION,
        source: operation.sender.pkh,
        delegate: void 0
      };
    case "fa1.2":
      return {
        kind: OpKind.TRANSACTION,
        amount: 0,
        to: operation.contract.pkh,
        parameter: makeFA12TransactionParameter(operation)
      };
    case "fa2":
      return {
        kind: OpKind.TRANSACTION,
        amount: 0,
        to: operation.contract.pkh,
        parameter: makeFA2TransactionParameter(operation)
      };
    case "contract_origination": {
      if (isValidMichelson(operation.storage)) {
        return {
          kind: OpKind.ORIGINATION,
          code: operation.code,
          init: operation.storage
        };
      }
      return {
        kind: OpKind.ORIGINATION,
        code: operation.code,
        storage: operation.storage
      };
    }
    case "stake":
      return {
        kind: OpKind.TRANSACTION,
        amount: parseInt(operation.amount),
        source: operation.sender.pkh,
        to: operation.sender.pkh,
        parameter: { entrypoint: "stake", value: { prim: "Unit" } },
        mutez: true
      };
    case "unstake":
      return {
        kind: OpKind.TRANSACTION,
        amount: parseInt(operation.amount),
        source: operation.sender.pkh,
        to: operation.sender.pkh,
        parameter: { entrypoint: "unstake", value: { prim: "Unit" } },
        mutez: true
      };
    case "finalize_unstake":
      return {
        kind: OpKind.TRANSACTION,
        amount: 0,
        source: operation.sender.pkh,
        to: operation.sender.pkh,
        parameter: { entrypoint: "finalize_unstake", value: { prim: "Unit" } }
      };
    case "safe_send":
    case "claim_safe_send":
    case "withdraw_safe_send":
    case "personal_sign":
    case "eth_sendTransaction":
    case "approve":
      throw new CustomError4(`${operation.type} is not supported yet`);
  }
};
var operationsToBatchParams = ({
  type: operationsType,
  operations: originalOperations,
  sender
}) => {
  const operations = operationsType === "implicit" ? originalOperations : [makeMultisigProposeOperation(sender.address, originalOperations)];
  return operations.map(operationToTaquitoOperation);
};
var operationsToWalletParams = (operations) => zipWith(operationsToBatchParams(operations), operations.estimates, (operation, estimate2) => ({
  ...operation,
  ...estimate2
}));
var withTimeout = (fn, timeout, errorMessage) => Promise.race([
  fn(),
  // it's safe to use the same type parameter T here
  // because we're going to throw anyway which stops the execution
  new Promise(
    (_, reject) => setTimeout(() => {
      reject(new CustomError4(errorMessage || "The operation has timed out"));
    }, timeout)
  )
]);

// src/estimate.ts
var estimate = async (operations, network) => {
  const tezosToolkit = await makeToolkit({
    type: "fake",
    signer: operations.signer,
    network
  });
  try {
    const estimates = await tezosToolkit.estimate.batch(operationsToBatchParams(operations));
    let revealEstimate = void 0;
    if (estimates.length > operations.operations.length) {
      revealEstimate = estimateToEstimation(estimates.shift());
    }
    return {
      ...operations,
      estimates: estimates.map(estimateToEstimation),
      revealEstimate
    };
  } catch (err) {
    const isRevealed = await isAccountRevealed(operations.signer.address.pkh, network);
    if (!isRevealed) {
      throw new CustomError2(`Signer address is not revealed on the ${network.name}.`);
    }
    throw err;
  }
};
var estimateToEstimation = (estimate2) => ({
  storageLimit: estimate2.storageLimit,
  gasLimit: estimate2.gasLimit,
  // The way taquito works we need to take the max of suggestedFeeMutez and totalCost
  // because the suggestedFeeMutez does not include the storage & execution cost
  // and in these cases the totalCost is the one to go (so, for contract calls)
  // though totalCost doesn't work well with simple tez transfers and suggestedFeeMutez is more accurate
  fee: Math.max(estimate2.suggestedFeeMutez, estimate2.totalCost)
});

// src/execute.ts
var executeOperations = async (operations, tezosToolkit) => {
  const params = operationsToWalletParams(operations);
  return tezosToolkit.wallet.batch(params).send();
};

// src/testUtils.tsx
import { ChainFullIds as ChainFullIds2 } from "@umami/chains";
import { uUSD } from "@umami/test-utils";
import {
  defaultDerivationPathTemplate,
  getDefaultDerivationPath,
  mockContractAddress,
  mockImplicitAddress,
  mockPk
} from "@umami/tezos";

// src/TokenBalance.ts
import { orderBy } from "lodash";

// src/Token.ts
import { ChainFullIds } from "@umami/chains";
import {
  FA12TokenSchema,
  FA2TokenSchema,
  NFTSchema
} from "@umami/tzkt";
import { BigNumber as BigNumber2 } from "bignumber.js";
import repeat from "lodash/repeat";
var fromRawToken = (rawToken) => {
  const metadata = rawToken.metadata;
  if (rawToken.standard === "fa1.2") {
    const fa1result = FA12TokenSchema.safeParse(rawToken);
    if (fa1result.success) {
      const contract = fa1result.data.contract.address;
      return {
        type: "fa1.2",
        metadata,
        contract,
        contractAlias: fa1result.data.contract.alias,
        tokenId: "0",
        chainId: ChainFullIds.TEZOS,
        id: `${contract}:0`
      };
    }
    console.warn("Invalid FA1 token: " + JSON.stringify(rawToken));
    return null;
  }
  const nftResult = NFTSchema.safeParse(rawToken);
  if (nftResult.success) {
    const contract = nftResult.data.contract.address;
    const tokenId = nftResult.data.tokenId;
    return {
      // if the nft has been parsed successfully then the metadata is definitely present
      metadata,
      type: "nft",
      id: `${contract}:${tokenId}`,
      contract,
      contractAlias: nftResult.data.contract.alias,
      tokenId,
      displayUri: nftResult.data.metadata.displayUri,
      totalSupply: nftResult.data.totalSupply,
      chainId: ChainFullIds.TEZOS
    };
  }
  const fa2result = FA2TokenSchema.safeParse(rawToken);
  if (fa2result.success) {
    const contract = fa2result.data.contract.address;
    const tokenId = fa2result.data.tokenId;
    return {
      type: "fa2",
      metadata,
      contract,
      contractAlias: fa2result.data.contract.alias,
      tokenId,
      chainId: ChainFullIds.TEZOS,
      id: `${contract}:${tokenId}`
    };
  }
  console.warn("Invalid FA2 token: " + JSON.stringify(rawToken));
  return null;
};
var fullId = (token) => `${token.contract}:${token.tokenId}`;
var defaultTokenName = (asset) => {
  switch (asset.type) {
    case "fa1.2":
      return DEFAULT_FA1_NAME;
    case "fa2":
      return DEFAULT_FA2_NAME;
    case "nft":
      return DEFAULT_NFT_NAME;
  }
};
var tokenNameSafe = (token) => tokenName(token) || defaultTokenName(token);
var tokenName = (token) => token.metadata?.name;
var defaultTokenSymbol = (token) => {
  switch (token.type) {
    case "fa1.2":
      return DEFAULT_FA1_SYMBOL;
    case "fa2":
      return DEFAULT_FA2_SYMBOL;
    case "nft":
      return DEFAULT_NFT_SYMBOL;
  }
};
var tokenSymbolSafe = (token) => tokenSymbol(token) || defaultTokenSymbol(token);
var tokenSymbol = (token) => token.metadata?.symbol;
var tokenDecimals = (asset) => asset.metadata?.decimals ?? DEFAULT_TOKEN_DECIMALS;
var getRealAmount = (token, prettyAmount) => {
  const amount = new BigNumber2(prettyAmount);
  const decimals = tokenDecimals(token);
  return amount.multipliedBy(new BigNumber2(10).exponentiatedBy(decimals)).toFixed();
};
var formatTokenAmount = (amount, decimals = DEFAULT_TOKEN_DECIMALS) => {
  const realAmount = BigNumber2(amount).dividedBy(BigNumber2(10).pow(decimals));
  try {
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: Number(decimals),
      maximumFractionDigits: Number(decimals)
    });
    return formatter.format(realAmount.toNumber());
  } catch (_) {
    console.warn(`Can't format token amount with decimals = ${decimals}`);
    return formatTokenAmount(amount, "0");
  }
};
var tokenPrettyAmount = (amount, token, options) => {
  if (token.type === "nft") {
    return amount;
  }
  const symbol = tokenSymbolSafe(token);
  const decimals = token.metadata?.decimals;
  const trailingSymbol = options?.showSymbol ? ` ${symbol}` : "";
  const result = formatTokenAmount(amount, decimals);
  return `${result}${trailingSymbol}`;
};
var artifactUri = (nft) => nft.metadata.artifactUri || nft.displayUri;
var thumbnailUri = (nft) => nft.metadata.thumbnailUri || nft.displayUri;
var mimeType = (nft) => nft.metadata.formats?.find((format) => format.uri === artifactUri(nft))?.mimeType;
var royalties = (nft) => {
  const royalties2 = nft.metadata.royalties;
  if (!royalties2) {
    return [];
  }
  const totalShares = Math.pow(10, Number(royalties2.decimals));
  const shares = Object.entries(royalties2.shares).map(([address, share]) => ({
    address,
    share: Number(share) * 100 / totalShares
  }));
  shares.sort((a, b) => a.share < b.share ? 1 : -1);
  return shares;
};
var metadataUri = (token, network) => `${tokenUri(token, network)}/metadata`;
var tokenUri = ({ contract, tokenId }, network) => `${network.explorerUrl}/${contract}/tokens/${tokenId}`;
var getSmallestUnit = (decimals) => {
  if (decimals < 0) {
    console.warn("Decimals cannot be negative");
    decimals = 0;
  }
  const leadingZeroes = decimals === 0 ? "" : "0." + repeat("0", decimals - 1);
  return `${leadingZeroes}1`;
};
var tokenContractName = (token) => token.contractAlias || token.contract;
var DEFAULT_FA1_NAME = "FA1.2 token";
var DEFAULT_FA2_NAME = "FA2 token";
var DEFAULT_NFT_NAME = "NFT";
var DEFAULT_FA1_SYMBOL = "FA1.2";
var DEFAULT_FA2_SYMBOL = "FA2";
var DEFAULT_NFT_SYMBOL = "NFT";
var DEFAULT_TOKEN_DECIMALS = "0";

// src/TokenBalance.ts
var fromRawTokenBalance = (raw) => {
  const token = fromRawToken(raw.token);
  if (!token || !raw.balance) {
    return null;
  }
  return { balance: raw.balance, lastLevel: raw.lastLevel, ...token };
};
var sortedByLastUpdate = (nfts) => orderBy(nfts, ["lastLevel", "id", "owner"], ["desc"]);

// src/testUtils.tsx
var mockDelegation = (id, initialAmount, delegateAddress, delegateAlias, date = /* @__PURE__ */ new Date()) => ({
  type: "delegation",
  id,
  level: 3278793,
  timestamp: date.toISOString(),
  block: "BMGrLsKz89GdctsFamoGErgKfBjt2P9aoroCQFmzqbDBbwwfKQZ",
  hash: "oo6eXUdtvpRsFDsNR9YE7zngbeCU89FsZfxQHNzMmiaNJHniF67",
  counter: 13186782,
  sender: { address: mockImplicitAddress(id).pkh },
  gasLimit: 1100,
  gasUsed: 1e3,
  storageLimit: 0,
  bakerFee: 396,
  amount: initialAmount,
  prevDelegate: {
    alias: "Pool of Stake",
    address: "tz1gjwq9ybKEmqQrTmzCoVB3HESYV1Ekc5up"
  },
  newDelegate: {
    alias: delegateAlias,
    address: delegateAddress
  },
  status: "applied"
});
var mockTokenTransaction = (id) => ({
  from: { address: `mockSender${id}` },
  to: { address: `mockTarget${id}` },
  transactionId: 100 + id
});
var mockAccountLabel = (accountIndex) => accountIndex === 0 ? "Account" : `Account ${accountIndex + 1}`;
var mockImplicitAccount = (index, type = "mnemonic", fingerPrint = "mockPrint", label) => {
  switch (type) {
    case "mnemonic":
      return mockMnemonicAccount(index, { label, seedFingerPrint: fingerPrint });
    case "social":
      return mockSocialAccount(index, label);
    case "ledger":
      return mockLedgerAccount(index, label);
    case "secret_key":
      return mockSecretKeyAccount(index, label);
  }
};
var mockMnemonicAccount = (index, options) => ({
  curve: "ed25519",
  derivationPath: getDefaultDerivationPath(index),
  derivationPathTemplate: "44'/1729'/?'/0'",
  type: "mnemonic",
  address: mockImplicitAddress(index),
  pk: mockPk(index),
  seedFingerPrint: "mockPrint",
  isVerified: true,
  ...options,
  label: options?.label || mockAccountLabel(index)
});
var mockSecretKeyAccount = (index, label) => ({
  type: "secret_key",
  curve: "ed25519",
  label: label || mockAccountLabel(index),
  address: mockImplicitAddress(index),
  pk: mockPk(index)
});
var mockSocialAccount = (index, label, idp) => ({
  type: "social",
  label: label || mockAccountLabel(index),
  address: mockImplicitAddress(index),
  pk: mockPk(index),
  idp: idp || "google"
});
var mockLedgerAccount = (index, label) => {
  const account = {
    type: "ledger",
    derivationPathTemplate: defaultDerivationPathTemplate,
    derivationPath: getDefaultDerivationPath(index),
    curve: "ed25519",
    label: label || mockAccountLabel(index),
    address: mockImplicitAddress(index),
    pk: mockPk(index)
  };
  return account;
};
var mockMultisigAccount = (index, signers) => ({
  type: "multisig",
  address: mockContractAddress(index),
  label: `Multisig Account ${index}`,
  threshold: 1,
  signers: signers || [mockImplicitAddress(index)],
  pendingOperationsBigmapId: index
});
var mockNFTToken = (index, ownerPkh, options) => ({
  id: index,
  balance: "1",
  account: {
    address: ownerPkh
  },
  token: {
    id: index,
    contract: {
      address: mockContractAddress(index).pkh
    },
    tokenId: String(index),
    standard: "fa2",
    totalSupply: "1",
    metadata: {
      name: "Tezzardz #10",
      creators: ["George Goodwin (@omgidrawedit)"],
      decimals: "0",
      royalties: {
        shares: {
          tz1LLPWMyZ7gKsp3WnLfemyAYW6CoZoozku5: "5"
        },
        decimals: "2"
      },
      description: "Tezzardz is a collection of 4,200 programmatically, randomly generated, snazzy little fukrs on the Tezos blockchain.",
      displayUri: `ipfs://zb2rhXWQ9X95yxQwusNjftDSWVQYbGjFFFFBjJKQZ8uCrNcvV${index}`
    }
  },
  ...options
});
var mockFA2Token = (index, account, balance = 1, decimals = 4, symbol = "KL2", name = "Klondike2") => fromRawTokenBalance(
  mockFA2TokenRaw(index, account.address.pkh, { balance, decimals, symbol, name })
);
var mockFA12Token = (index, account, balance = 1) => fromRawTokenBalance(mockFA1TokenRaw(index, account.address.pkh, balance));
var mockFA2TokenRaw = (index, owner, tokenParams) => {
  const { balance, decimals, symbol, name, lastLevel } = {
    balance: 1,
    decimals: 4,
    symbol: "KL2",
    name: "Klondike2",
    lastLevel: 2247548,
    ...tokenParams
  };
  return {
    id: 10898270846977,
    account: {
      address: owner
    },
    token: {
      id: 10898194300929,
      contract: {
        address: mockContractAddress(index).pkh
      },
      tokenId: String(index),
      standard: "fa2",
      totalSupply: "13000000000",
      metadata: {
        name,
        symbol,
        decimals: String(decimals)
      }
    },
    balance: String(balance),
    transfersCount: 27,
    firstLevel: 288245,
    firstTime: "2022-03-24T15:36:50Z",
    lastLevel,
    lastTime: "2023-03-31T11:19:01Z"
  };
};
var mockFA1TokenRaw = (index, owner, balance = 1) => ({
  id: 10897662672897,
  account: {
    address: owner
  },
  token: {
    id: 10897625972737,
    contract: {
      address: mockContractAddress(index).pkh
    },
    tokenId: "0",
    standard: "fa1.2",
    totalSupply: "13000000"
  },
  balance: String(balance),
  transfersCount: 28,
  firstLevel: 288229,
  firstTime: "2022-03-24T15:32:10Z",
  lastLevel: 2215201,
  lastTime: "2023-03-27T13:30:37Z"
});
var mockNFTRaw = (index, owner, args) => {
  const tokenBalance = mockFA2TokenRaw(index, owner, { ...args, decimals: 0 });
  tokenBalance.token.metadata.displayUri = mockDisplayURI(index);
  return tokenBalance;
};
var mockDisplayURI = (index) => `ipfs://zdj7Wk92xWxpzGqT6sE4cx7umUyWaX2Ck8MrSEmPAR31sNWG${index}`;
var mockNFTBalance = (index, options) => {
  const displayUri = mockDisplayURI(index);
  return {
    id: `${mockContractAddress(index).pkh}:mockId${index}`,
    type: "nft",
    balance: "1",
    displayUri,
    contract: mockContractAddress(index).pkh,
    tokenId: "mockId" + index,
    totalSupply: "1",
    metadata: {
      displayUri,
      name: "Tezzardz #" + index,
      symbol: "FKR" + index
    },
    chainId: ChainFullIds2.TEZOS,
    ...options
  };
};
var mockBaker = (index) => ({
  name: `label${index}`,
  address: mockImplicitAddress(index).pkh,
  stakingBalance: 1e5 + index
});
var mockTezOperation = (index) => ({
  type: "tez",
  amount: String(index),
  recipient: mockImplicitAddress(index + 1)
});
var mockFA12Operation = (index) => ({
  type: "fa1.2",
  amount: String(index),
  sender: mockImplicitAddress(index),
  recipient: mockImplicitAddress(index + 1),
  contract: mockContractAddress(index),
  tokenId: "0"
});
var mockNftOperation = (index) => ({
  type: "fa2",
  amount: String(index),
  sender: mockImplicitAddress(index),
  recipient: mockImplicitAddress(index + 1),
  contract: mockContractAddress(index),
  tokenId: String(index)
});
var mockFA2Operation = mockNftOperation;
var mockDelegationOperation = (index) => ({
  type: "delegation",
  sender: mockImplicitAddress(index),
  recipient: mockImplicitAddress(index + 1)
});
var mockUndelegationOperation = (index) => ({
  type: "undelegation",
  sender: mockImplicitAddress(index)
});
var mockContractOrigination = (index, storage = {}, code = []) => ({
  type: "contract_origination",
  storage,
  code,
  sender: mockImplicitAddress(index)
});
var mockContractCall = (index, code = []) => ({
  type: "contract_call",
  entrypoint: "test-entrypoint",
  amount: String(index),
  contract: mockContractAddress(index),
  args: code
});
var mockStakeOperation = (index) => ({
  type: "stake",
  amount: String(index),
  sender: mockImplicitAddress(index)
});
var mockUnstakeOperation = (index) => ({
  type: "unstake",
  amount: String(index),
  sender: mockImplicitAddress(index)
});
var mockFinalizeUnstakeOperation = (index) => ({
  type: "finalize_unstake",
  sender: mockImplicitAddress(index)
});
var mockImplicitContact = (index, label) => ({
  name: label || `Contact ${index}`,
  chainId: "tezos",
  pkh: mockImplicitAddress(index).pkh,
  network: void 0
});
var mockContractContact = (index, network, label) => ({
  name: label || `Contact ${index}`,
  pkh: mockContractAddress(index).pkh,
  network,
  chainId: "tezos"
});
var rawAccountFixture = (props) => ({
  address: mockImplicitAddress(0).pkh,
  balance: 1e6,
  delegate: { alias: "mega_baker", address: mockImplicitAddress(1).pkh },
  stakedBalance: 0,
  unstakedBalance: 0,
  rollupBonds: 0,
  smartRollupBonds: 0,
  ...props
});
var transactionFixture = (props) => ({
  amount: 1,
  target: { address: mockImplicitAccount(0).address.pkh },
  sender: { address: mockImplicitAccount(1).address.pkh },
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  timestamp: "2021-01-02T00:00:00.000Z",
  type: "transaction",
  status: "applied",
  ...props
});
var contractCallFixture = (props) => transactionFixture({
  target: { address: mockContractAddress(0).pkh },
  parameter: { entrypoint: "test-entrypoint" },
  ...props
});
var originationFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "origination",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  originatedContract: {
    address: mockContractAddress(0).pkh,
    typeHash: 1963879877,
    codeHash: -1890025422
  },
  status: "applied",
  ...props
});
var delegationFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "delegation",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  newDelegate: {
    address: mockImplicitAddress(1).pkh
  },
  amount: 5,
  status: "applied",
  ...props
});
var tokenTransferFixture = (props) => ({
  type: "token_transfer",
  amount: "500",
  transactionId: 56789,
  id: 278346,
  level: 10,
  from: { address: mockImplicitAccount(0).address.pkh },
  to: { address: mockImplicitAccount(1).address.pkh },
  token: uUSD(mockLedgerAccount(0).address).token,
  ...props
});
var stakeFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "stake",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  baker: {
    address: mockImplicitAddress(1).pkh
  },
  amount: 5,
  status: "applied",
  ...props
});
var unstakeFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "unstake",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  baker: {
    address: mockImplicitAddress(1).pkh
  },
  amount: 10,
  status: "applied",
  ...props
});
var finalizeUnstakeFixture = (props) => ({
  hash: "test-hash",
  counter: 1234,
  id: 56789,
  level: 4321,
  type: "finalize",
  sender: { address: mockLedgerAccount(0).address.pkh },
  timestamp: "2021-01-02T00:00:00.000Z",
  amount: 15,
  status: "applied",
  ...props
});

// src/beaconUtils.ts
import { TezosOperationType } from "@tezos-x/octez.connect-wallet";
import { isValidImplicitPkh, parseImplicitPkh, parsePkh } from "@umami/tezos";
import { WalletConnectError, WcErrorCode } from "@umami/utils";
var toAccountOperations = (operationDetails, signer) => {
  if (operationDetails.length === 0) {
    throw new WalletConnectError("Empty operation details!", WcErrorCode.INVALID_PARAMS, null);
  }
  const operations = operationDetails.map(
    (operation) => partialOperationToOperation(operation, signer)
  );
  return {
    type: "implicit",
    sender: signer,
    operations,
    signer
  };
};
var partialOperationToOperation = (partialOperation, signer) => {
  switch (partialOperation.kind) {
    case TezosOperationType.TRANSACTION: {
      const { destination, amount, parameters } = partialOperation;
      if (parameters) {
        if (isValidImplicitPkh(destination)) {
          switch (parameters.entrypoint) {
            case "stake":
              return { type: "stake", amount, sender: parseImplicitPkh(destination) };
            case "unstake":
              return { type: "unstake", amount, sender: parseImplicitPkh(destination) };
            case "finalize_unstake":
              return { type: "finalize_unstake", sender: parseImplicitPkh(destination) };
          }
        }
        return {
          type: "contract_call",
          amount,
          contract: parsePkh(destination),
          entrypoint: parameters.entrypoint,
          args: parameters.value
        };
      }
      return {
        type: "tez",
        amount,
        recipient: parseImplicitPkh(partialOperation.destination)
      };
    }
    case TezosOperationType.DELEGATION: {
      const { delegate } = partialOperation;
      if (delegate) {
        return {
          type: "delegation",
          sender: signer.address,
          recipient: parseImplicitPkh(delegate)
        };
      }
      return { type: "undelegation", sender: signer.address };
    }
    case TezosOperationType.ORIGINATION: {
      const { script } = partialOperation;
      const { code, storage } = script;
      return {
        type: "contract_origination",
        sender: signer.address,
        code,
        storage
      };
    }
    default:
      throw new WalletConnectError(
        `Unsupported operation kind: ${partialOperation.kind}`,
        WcErrorCode.METHOD_UNSUPPORTED,
        null
      );
  }
};

// src/getAccountGroupLabel.ts
var getAccountGroupLabel = (account) => {
  switch (account.type) {
    case "mnemonic":
      return `Seedphrase ${account.seedFingerPrint}`;
    case "social":
      return "Social accounts";
    case "ledger":
      return "Ledger accounts";
    case "secret_key":
      return "Secret key accounts";
    case "multisig":
      return "Multisig accounts";
  }
};
export {
  DEFAULT_ACCOUNT_LABEL,
  FA12_TRANSFER_ARG_TYPES,
  FA2_TRANSFER_ARG_TYPES,
  Hints,
  SafeSendAssetType,
  Titles,
  artifactUri,
  contractCallFixture,
  decodeBeaconPayload,
  delegationFixture,
  estimate,
  executeOperations,
  finalizeUnstakeFixture,
  formatTokenAmount,
  fromRawToken,
  fromRawTokenBalance,
  fullId,
  getAccountGroupLabel,
  getRealAmount,
  getSigningTypeFromPayload,
  getSmallestUnit,
  headlessLambda,
  makeAccountOperations,
  makeFA12TransactionParameter,
  makeFA2TransactionParameter,
  makeMultisigApproveOrExecuteOperation,
  makeMultisigProposeOperation,
  metadataUri,
  mimeType,
  mockAccountLabel,
  mockBaker,
  mockContractCall,
  mockContractContact,
  mockContractOrigination,
  mockDelegation,
  mockDelegationOperation,
  mockFA12Operation,
  mockFA12Token,
  mockFA1TokenRaw,
  mockFA2Operation,
  mockFA2Token,
  mockFA2TokenRaw,
  mockFinalizeUnstakeOperation,
  mockImplicitAccount,
  mockImplicitContact,
  mockLedgerAccount,
  mockMnemonicAccount,
  mockMultisigAccount,
  mockNFTBalance,
  mockNFTRaw,
  mockNFTToken,
  mockNftOperation,
  mockSecretKeyAccount,
  mockSocialAccount,
  mockStakeOperation,
  mockTezOperation,
  mockTokenTransaction,
  mockUndelegationOperation,
  mockUnstakeOperation,
  operationSign,
  operationToTaquitoOperation,
  operationsToBatchParams,
  operationsToWalletParams,
  originationFixture,
  partialOperationToOperation,
  rawAccountFixture,
  royalties,
  selectRandomElements,
  sortedByLastUpdate,
  stakeFixture,
  thumbnailUri,
  toAccountOperations,
  toBatchLambda,
  toLambda,
  tokenContractName,
  tokenDecimals,
  tokenName,
  tokenNameSafe,
  tokenPrettyAmount,
  tokenSymbol,
  tokenSymbolSafe,
  tokenTransferFixture,
  tokenUri,
  totalFee,
  transactionFixture,
  unstakeFixture,
  withTimeout
};
